import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectSSEState,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  clearInvoiceUpdates,
  manualRetryRequested,
} from 'src/infra/stores/reducer/sseSlice';
import type { AppDispatch } from 'src/infra/stores/store';

/**
 * Hook for accessing SSE state and actions from Redux
 *
 * NOTE: This hook does NOT create SSE connections!
 * Connection management is handled by SSEInitializer component.
 * This hook only provides access to Redux state and actions.
 */
export const useSSE = () => {
  const dispatch = useDispatch<AppDispatch>();
  const sseState = useSelector(selectSSEState);

  // Action dispatchers
  const markNotificationAsReadCallback = useCallback(() => {
    dispatch(markNotificationAsRead());
  }, [dispatch]);

  const markAllAsReadCallback = useCallback(() => {
    dispatch(markAllNotificationsAsRead());
  }, [dispatch]);

  const clearInvoiceUpdatesCallback = useCallback(() => {
    dispatch(clearInvoiceUpdates());
  }, [dispatch]);

  const manualRetryCallback = useCallback(() => {
    dispatch(manualRetryRequested());
  }, [dispatch]);

  // Return Redux state and action dispatchers
  return {
    // State
    invoiceUpdates: sseState.invoiceUpdates,
    notificationStatus: sseState.notificationStatus,
    isConnected: sseState.isConnected,
    retryState: sseState.retryState,
    lastActivity: sseState.lastActivity,

    // Actions
    markNotificationAsRead: markNotificationAsReadCallback,
    markAllAsRead: markAllAsReadCallback,
    clearInvoiceUpdates: clearInvoiceUpdatesCallback,
    manualRetry: manualRetryCallback,
  };
};
